import { formatDate } from '@angular/common';
import { enumHtsVerificationSource } from '@data-layer/enums/common';
import {
  HtsChatConversationModel,
  HtsChatEntryLineModel,
  HtsChatLineVerificationModel,
  HtsChatRemovedLineModel,
  ShipmentModel,
} from '@data-layer/models/common';
import { EntrySummaryModel, InvoiceModel, LineModel } from '@data-layer/models/entry-summaries';
import { enumHtsLineStatus } from '../enums';
import { IHtsAssistantLineRef, IHtsLineReason } from '../interfaces';

/** Date of a catalog-backed verification in its reason text. */
const REASON_DATE_FORMAT: string = 'MM/dd/yyyy';

/**
 * Line verification status for the "Validate HTS codes" flow (#13384). Pure: the status comes ONLY from the backend
 * verification fields — the frontend computes nothing, it just reads and counts.
 */
export class HtsLineStatusHelper {
  public static getStatus(line: LineModel): enumHtsLineStatus {
    const source: enumHtsVerificationSource = line.htsVerificationSource || enumHtsVerificationSource.None;
    if (source === enumHtsVerificationSource.None) {
      return enumHtsLineStatus.NeedsClassification;
    }
    // "no answer" (with or without a code typed afterwards) and a cleared validated code pass the gate, but are not "validated"
    if (source === enumHtsVerificationSource.AssistantNoAnswer || line.htsCodeRemoved) {
      return enumHtsLineStatus.PassesWithoutCode;
    }
    return enumHtsLineStatus.Validated;
  }

  public static isNeedsClassification(line: LineModel): boolean {
    return HtsLineStatusHelper.getStatus(line) === enumHtsLineStatus.NeedsClassification;
  }

  public static isNeedsClassificationRef(line: IHtsAssistantLineRef): boolean {
    return line.status === enumHtsLineStatus.NeedsClassification;
  }

  /** The reason shown next to / under the status chip (texts of #13384); `removedHtsNumber` is known on the history read only. */
  public static getReason(line: LineModel, removedHtsNumber: string | null = null): IHtsLineReason {
    const source: enumHtsVerificationSource = line.htsVerificationSource || enumHtsVerificationSource.None;
    if (source !== enumHtsVerificationSource.None && source !== enumHtsVerificationSource.AssistantNoAnswer && line.htsCodeRemoved) {
      return removedHtsNumber
        ? { key: 'HTS_ASSISTANT.REASON_CODE_REMOVED_KNOWN', params: { code: removedHtsNumber } }
        : { key: 'HTS_ASSISTANT.REASON_CODE_REMOVED', params: null };
    }
    switch (source) {
      case enumHtsVerificationSource.Assistant:
        return { key: 'HTS_ASSISTANT.REASON_ASSISTANT', params: null };
      case enumHtsVerificationSource.Catalog:
        return line.htsVerifiedDate
          ? {
              key: 'HTS_ASSISTANT.REASON_CATALOG_DATED',
              params: { date: formatDate(line.htsVerifiedDate, REASON_DATE_FORMAT, 'en-US') },
            }
          : { key: 'HTS_ASSISTANT.REASON_CATALOG', params: null };
      case enumHtsVerificationSource.CompanyHistory:
        return { key: 'HTS_ASSISTANT.REASON_COMPANY_HISTORY', params: null };
      case enumHtsVerificationSource.PartNumberMapping:
        return { key: 'HTS_ASSISTANT.REASON_PART_MAPPING', params: { partNumber: line.htsVerificationPartNumber || '' } };
      case enumHtsVerificationSource.Broker:
        return { key: 'HTS_ASSISTANT.REASON_BROKER', params: null };
      case enumHtsVerificationSource.SimilarityConfirmed:
        return { key: 'HTS_ASSISTANT.REASON_SIMILARITY', params: { matchedText: line.htsVerificationMatchedText || '' } };
      case enumHtsVerificationSource.AssistantNoAnswer:
        return {
          key: line.htsNumber ? 'HTS_ASSISTANT.REASON_CODE_AFTER_NO_ANSWER' : 'HTS_ASSISTANT.REASON_NO_ANSWER',
          params: null,
        };
      default:
        if (!line.description) {
          return { key: 'HTS_ASSISTANT.REASON_DESCRIPTION_MISSING', params: null };
        }
        return { key: line.htsNumber ? 'HTS_ASSISTANT.REASON_NOT_VERIFIED' : 'HTS_ASSISTANT.REASON_HTS_MISSING', params: null };
    }
  }

  public static countNeedsClassification(shipment: ShipmentModel): number {
    return HtsLineStatusHelper.linesOf(shipment).filter((line: LineModel) => HtsLineStatusHelper.isNeedsClassification(line)).length;
  }

  public static countNeedsClassificationInEntry(entrySummary: EntrySummaryModel): number {
    return (entrySummary.shipments || []).reduce(
      (sum: number, shipment: ShipmentModel) => sum + HtsLineStatusHelper.countNeedsClassification(shipment),
      0,
    );
  }

  /** History (#13385): the entry-level read already carries every field the stepper needs. */
  public static fromHistory(lines: HtsChatEntryLineModel[]): IHtsAssistantLineRef[] {
    const billIds: string[] = [];
    return lines.map((line: HtsChatEntryLineModel, index: number) => {
      const bill: string = line.billOfLading || '';
      if (!billIds.includes(bill)) {
        billIds.push(bill);
      }
      const asLine: LineModel = HtsLineStatusHelper.historyLineAsLine(line);
      const reason: IHtsLineReason = HtsLineStatusHelper.getReason(asLine, line.verification ? line.verification.removedHtsNumber : null);
      return {
        lineId: line.lineId,
        index: index + 1,
        shipmentId: billIds.indexOf(bill) + 1, // the history has no shipment id: one synthetic id per Bill of Lading
        invoiceId: line.invoiceId,
        description: line.description || null,
        htsNumber: line.htsNumber || null,
        verificationSource: asLine.htsVerificationSource,
        matchedText: asLine.htsVerificationMatchedText || null,
        status: HtsLineStatusHelper.getStatus(asLine),
        reasonKey: reason.key,
        reasonParams: reason.params,
      };
    });
  }

  /** History (#13385): lines deleted after they had conversations, listed after the entry's lines with their last description. */
  public static fromRemoved(removedLines: HtsChatRemovedLineModel[], startIndex: number): IHtsAssistantLineRef[] {
    return removedLines.map((line: HtsChatRemovedLineModel, index: number) => ({
      lineId: line.lineId,
      index: startIndex + index + 1,
      shipmentId: 0,
      invoiceId: 0,
      description: line.description || null,
      htsNumber: null,
      verificationSource: enumHtsVerificationSource.None,
      matchedText: null,
      status: enumHtsLineStatus.NeedsClassification,
      reasonKey: 'HTS_ASSISTANT.REASON_LINE_REMOVED',
      reasonParams: null,
      isRemoved: true,
    }));
  }

  /** The conversation the do-over history opens on (admin, Broker Review): the one whose result was applied, else the latest. */
  public static finalConversationId(line: HtsChatEntryLineModel): number | null {
    const verification: HtsChatLineVerificationModel | null = line.verification;
    const applied: HtsChatConversationModel | undefined =
      verification && verification.source === enumHtsVerificationSource.Assistant && verification.conversationId
        ? (line.conversations || []).find((c: HtsChatConversationModel) => c.id === verification.conversationId)
        : undefined;
    return applied ? applied.id : HtsLineStatusHelper.latestConversationId(line.conversations);
  }

  /** The latest conversation by start date — the do-over's final one where no verification says otherwise (removed lines). */
  public static latestConversationId(conversations: HtsChatConversationModel[] | null): number | null {
    const latest: HtsChatConversationModel | undefined = [...(conversations || [])].sort(
      (a: HtsChatConversationModel, b: HtsChatConversationModel) =>
        new Date(b.startedDate).getTime() - new Date(a.startedDate).getTime() || b.id - a.id,
    )[0];
    return latest ? latest.id : null;
  }

  /** The stored model of a saved line (null when the entry does not hold it, e.g. a page without the entry state). */
  public static findLineModel(entrySummary: EntrySummaryModel | null, lineId: number): LineModel | null {
    if (!entrySummary) {
      return null;
    }
    for (const shipment of entrySummary.shipments || []) {
      for (const invoice of shipment.invoices || []) {
        const line: LineModel | undefined = (invoice.lines || []).find((l: LineModel) => l.id === lineId);
        if (line) {
          return line;
        }
      }
    }
    return null;
  }

  /** Every line of the entry in BL → invoice → line order. */
  public static flattenLines(entrySummary: EntrySummaryModel): IHtsAssistantLineRef[] {
    const refs: IHtsAssistantLineRef[] = [];
    (entrySummary.shipments || []).forEach((shipment: ShipmentModel) => {
      (shipment.invoices || []).forEach((invoice: InvoiceModel) => {
        (invoice.lines || []).forEach((line: LineModel) => {
          refs.push(HtsLineStatusHelper.toLineRef(line, shipment, invoice, refs.length + 1));
        });
      });
    });
    return refs;
  }

  /**
   * The line the view should open on: first unverified line of the clicked invoice, else of the clicked BL, else the
   * first unverified line of the entry, else the first line.
   */
  public static pickFocusLine(
    lines: IHtsAssistantLineRef[],
    focusShipmentId: number | null,
    focusInvoiceId: number | null = null,
  ): IHtsAssistantLineRef | null {
    const isNeeds: (line: IHtsAssistantLineRef) => boolean = HtsLineStatusHelper.isNeedsClassificationRef;
    const inInvoice: IHtsAssistantLineRef = focusInvoiceId
      ? lines.find((line: IHtsAssistantLineRef) => line.invoiceId === focusInvoiceId && isNeeds(line))
      : null;
    const inShipment: IHtsAssistantLineRef = focusShipmentId
      ? lines.find((line: IHtsAssistantLineRef) => line.shipmentId === focusShipmentId && isNeeds(line))
      : null;
    return inInvoice || inShipment || lines.find(isNeeds) || lines[0] || null;
  }

  /** Enough of a `LineModel` for the shared status / reason rules. */
  private static historyLineAsLine(line: HtsChatEntryLineModel): LineModel {
    const verification: HtsChatLineVerificationModel | null = line.verification;
    return {
      id: line.lineId,
      description: line.description,
      htsNumber: line.htsNumber,
      htsVerificationSource: verification ? verification.source : enumHtsVerificationSource.None,
      htsVerificationSourceLineId: verification ? verification.sourceLineId : null,
      htsVerificationPartNumber: verification ? verification.partNumber : null,
      htsVerificationMatchedText: verification ? verification.matchedText : null,
      htsVerifiedDate: verification ? verification.verifiedDate : null,
      htsCodeRemoved: verification ? verification.codeRemoved : false,
      htsCodeEnteredAfterNoAnswer: verification ? verification.codeEnteredAfterNoAnswer : false,
    } as LineModel;
  }

  private static toLineRef(line: LineModel, shipment: ShipmentModel, invoice: InvoiceModel, index: number): IHtsAssistantLineRef {
    const reason: IHtsLineReason = HtsLineStatusHelper.getReason(line);
    return {
      lineId: line.id,
      index,
      shipmentId: shipment.id,
      invoiceId: invoice.id,
      description: line.description || null,
      htsNumber: line.htsNumber || null,
      verificationSource: line.htsVerificationSource || enumHtsVerificationSource.None,
      matchedText: line.htsVerificationMatchedText || null,
      status: HtsLineStatusHelper.getStatus(line),
      reasonKey: reason.key,
      reasonParams: reason.params,
    };
  }

  private static linesOf(shipment: ShipmentModel): LineModel[] {
    return (shipment.invoices || []).reduce((lines: LineModel[], invoice: InvoiceModel) => [...lines, ...(invoice.lines || [])], []);
  }
}
